import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { productTypes } from '../../types/products';

import { TranslatedCopy } from '../../intl/TranslatedCopy';

import { H2, ProductGenderDivision, Currency, SpacerDot, Table } from '../../components';
import { ProductImage } from '../../components/aurora';

import { toTitleCase } from '../../util/toTitleCase';

import { createProductName } from '../../util/createProductName';
import sizeChartColumns from './sizeChartTableColumns';

const rsoSizeChartColumns = [
  ...sizeChartColumns,
  {
    header: <TranslatedCopy id='Received Quantity' />,
    id: 'receivedQuantity',
    cell: size => {
      return <span className='text-color-secondary'>{size.receivedQty === 0 ? 0 : size.receivedQty}</span>;
    }
  }
];

const ReturnDetailProductCard = ({ product, currencyCode, isGreaterChinaSalesOrg, sizeData, hasSubRSOs }) => {
  const {
    id,
    copy,
    productCode,
    division,
    styleGenderAge,
    colorName,
    retailAmount,
    quantity,
    receivedQty,
    netAmount,
    dimensions,
    sizeCode
  } = product;
  const formattedColor = toTitleCase(
    toTitleCase(colorName?.replace(/\//g, ' ')).replace(/\s+/g, '/').replace(/-/g, ' '),
    false
  ).replace(/\s+/g, '-');

  let netTotal;

  if (hasSubRSOs) {
    netTotal = netAmount && receivedQty ? netAmount * receivedQty : '0';
  } else {
    netTotal = netAmount && quantity ? netAmount * quantity : '0';
  }

  netTotal = netTotal.toFixed(2);

  return (
    <div className='orderReturns__select_product mb3-sm bg-white'>
      <div className='flex-container row start p5-sm prl10-md'>
        <div className='orderReturns__select_product_img p3-sm border'>
          <ProductImage alt={productCode} productCode={productCode} />
        </div>
        <div
          className={classnames(
            'orderReturns__select_product_info',
            'ml5-sm flex-container column start space-between u-full-width'
          )}
        >
          <div className='pr0-sm pr5-lg'>
            <H2>
              {createProductName({
                name: copy?.name,
                dimensions,
                division
              })}
            </H2>
            <ul className='body-4 mt2-sm pr1-sm text-color-warm-grey'>
              <li>
                <p>
                  <ProductGenderDivision gender={styleGenderAge} division={division} />
                </p>
              </li>
              <li>
                <p>
                  <TranslatedCopy id='global.product.style' />
                  {': '}
                  {productCode}
                </p>
              </li>
              <li>
                <p>
                  <TranslatedCopy id='global.product.color' />
                  {': '}
                  {formattedColor}
                </p>
              </li>
              <li>
                <p>
                  <TranslatedCopy id='global.size' /> {sizeCode}
                </p>
              </li>
              <li>
                <p>
                  {!isGreaterChinaSalesOrg && (
                    <>
                      <TranslatedCopy id='global.product.retail' />{' '}
                      <Currency value={retailAmount || '0'} type={currencyCode} />
                      <SpacerDot />
                    </>
                  )}
                  {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                  <TranslatedCopy id='global.product.wholesale.net' />{' '}
                  <Currency value={netAmount || '0'} type={currencyCode} />
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className='flex-container start p2-md pl10-md pl4-sm body-4 text-color-warm-grey'>
        <div className='orderReturns__select_productTotals text-color-primary-dark'>
          <Table
            id={id}
            overrideClass={classnames('cart__table ', 'orderReturns__select_productTotals')}
            columns={hasSubRSOs ? rsoSizeChartColumns : sizeChartColumns}
            data={sizeData}
          />
          <div className='pt4-sm pr4-md pb4-sm pt4-md pb0-md ta-sm-r'>
            <H2>
              <TranslatedCopy id='orderreturns.totalQuantity' />
              {': '}
              {quantity}
            </H2>
            {hasSubRSOs && (
              <H2>
                <TranslatedCopy id='Received Qty' />
                {': '}
                {receivedQty}
              </H2>
            )}
            <H2>
              <TranslatedCopy id='orderreturns.netAmount' />
              {': '}
              <Currency value={netTotal} type={currencyCode} />
            </H2>
          </div>
        </div>
      </div>
    </div>
  );
};

ReturnDetailProductCard.propTypes = {
  product: productTypes.isRequired,
  currencyCode: PropTypes.string.isRequired,
  isGreaterChinaSalesOrg: PropTypes.bool.isRequired,
  sizeData: PropTypes.array.isRequired,
  hasSubRSOs: PropTypes.bool.isRequired
};

export default ReturnDetailProductCard;
